/**
 * Genera el PDF de Contrato Proveedor (versión interna con costos).
 * Solo usado por admin.
 */
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { PICPARTY_LOGO_BASE64 } from "../../../lib/picpartyLogo";
import { parseLocalDate } from "../../../lib/dateUtils";
import { AVISO_URL } from "../../../lib/avisoPrivacidad";

export const printProveedorPDF = async (contract) => {
    let qrDataUrl = null;
    try {
      const QRCode = (await import("qrcode")).default;
      qrDataUrl = await QRCode.toDataURL(AVISO_URL, { width: 300, margin: 1, errorCorrectionLevel: "M", color: { dark: "#000000", light: "#FFFFFF" } });
    } catch (e) {
      console.error("Error generando QR del aviso de privacidad:", e);
    }

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 15;

    // Header
    pdf.setFillColor(26, 11, 46);
    pdf.rect(0, 0, pageWidth, 35, 'F');
    pdf.setFontSize(18);
    pdf.setTextColor(255, 215, 0);
    pdf.setFont(undefined, 'bold');
    pdf.text("CONTRATO PROVEEDOR", pageWidth / 2, 15, { align: 'center' });
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'normal');
    pdf.text("DOCUMENTO INTERNO - PIC PARTY", pageWidth / 2, 25, { align: 'center' });

    // QR del aviso de privacidad (mínimo 2cm, sobre cuadro blanco)
    if (qrDataUrl) {
      try {
        const qrSize = 20;
        const qrX = pageWidth - margin - qrSize;
        pdf.setFillColor(255, 255, 255);
        pdf.rect(qrX - 1, 38, qrSize + 2, qrSize + 11, 'F');
        pdf.addImage(qrDataUrl, 'PNG', qrX, 39, qrSize, qrSize);
        pdf.setTextColor(60, 60, 60);
        pdf.setFontSize(5.5);
        pdf.setFont(undefined, 'bold');
        pdf.text("AVISO DE PRIVACIDAD", qrX + qrSize / 2, 39 + qrSize + 3, { align: 'center' });
        pdf.setFont(undefined, 'normal');
        pdf.setFontSize(5);
        pdf.setTextColor(110, 110, 110);
        pdf.text("Escanea para consultarlo", qrX + qrSize / 2, 39 + qrSize + 6.3, { align: 'center' });
      } catch (e) {
        console.error("Error insertando QR de privacidad en PDF:", e);
      }
    }

    let y = 50;
    pdf.setTextColor(0, 0, 0);

    // Fecha del evento: parsear como fecha local (AAAA-MM-DD sin hora) para que el
    // día de la semana no se recorra por interpretación UTC.
    let fechaEvento = contract.event_date || 'N/A';
    try {
      const dateObj = parseLocalDate(contract.event_date);
      if (dateObj) {
        fechaEvento = dateObj.toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      }
    } catch (e) {}

    // Datos del Evento
    pdf.setFontSize(12);
    pdf.setFont(undefined, 'bold');
    pdf.text("DATOS DEL EVENTO", margin, y);
    y += 8;
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(10);
    pdf.text(`Cliente: ${contract.client_name || 'N/A'}`, margin, y); y += 6;
    pdf.text(`Fecha: ${fechaEvento}`, margin, y); y += 6;
    pdf.text(`Horario Evento: ${contract.event_time || 'N/A'}`, margin, y); y += 6;
    if (contract.service_time_comment) {
      pdf.text(`Inicio Servicio: ${contract.service_time || 'N/A'}`, margin, y); y += 5;
      pdf.setFont(undefined, 'bold');
      const comentLines = pdf.splitTextToSize(`Comentario: ${contract.service_time_comment}`, pageWidth - margin * 2);
      pdf.text(comentLines, margin, y); y += comentLines.length * 4.5 + 1;
      pdf.setFont(undefined, 'normal');
    } else {
      pdf.text(`Inicio Servicio: ${contract.service_time || 'N/A'}`, margin, y); y += 6;
    }
    pdf.text(`Teléfono: ${contract.client_phone || 'N/A'}`, margin, y); y += 12;

    // Servicios Contratados
    pdf.setFont(undefined, 'bold');
    pdf.setFontSize(12);
    pdf.text("SERVICIOS CONTRATADOS", margin, y);
    y += 8;
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(10);

    if (contract.include_cabina) {
      pdf.text(`✓ Cabina de Fotos - ${contract.cabina_hours || 3} horas`, margin, y);
      y += 6;
    }
    if (contract.include_video360) {
      pdf.text(`✓ Video 360 - ${contract.video360_hours || 3} horas`, margin, y);
      y += 6;
    }
    if (contract.include_key_moments) {
      pdf.text(`✓ Key Moments - ${contract.key_moments_pieces || 50} piezas`, margin, y);
      y += 6;
    }
    if (contract.include_live) {
      pdf.text(`✓ PicPartyLive`, margin, y);
      y += 6;
    }

    y += 10;

    // Costos Proveedor (vacío para llenar)
    pdf.setFont(undefined, 'bold');
    pdf.setFontSize(12);
    pdf.text("COSTOS PROVEEDOR", margin, y);
    y += 8;

    pdf.setDrawColor(200);
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(10);
    
    const services = ["Cabina de Fotos", "Video 360", "Key Moments", "Transporte/Flete", "TOTAL"];
    services.forEach(s => {
      pdf.text(s + ":", margin, y);
      pdf.line(margin + 50, y, pageWidth - margin, y);
      y += 8;
    });

    y += 10;

    // Firmas
    pdf.setFont(undefined, 'bold');
    pdf.text("FIRMA PROVEEDOR:", margin, y);
    pdf.line(margin + 50, y, margin + 120, y);
    y += 15;
    pdf.text("FIRMA PIC PARTY:", margin, y);
    pdf.line(margin + 50, y, margin + 120, y);

    // Footer
    pdf.setFontSize(8);
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(100);
    pdf.text(`Generado: ${new Date().toLocaleString('es-MX')} | PIC PARTY`, pageWidth / 2, 270, { align: 'center' });

    // Descarga compatible con móviles
    const fileName = `Proveedor_${contract.client_name?.replace(/\s+/g, '_') || 'contrato'}.pdf`;
    const pdfBlob = pdf.output('blob');
    const blobUrl = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
    toast.success("Contrato Proveedor generado");
  };

